import React, { useEffect, useMemo, useState } from "react";

const API_BASE = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || "";

function getToken() {
  return localStorage.getItem("token") || localStorage.getItem("access_token") || localStorage.getItem("authToken") || "";
}

function formatSydney(value) {
  if (!value) return "N/A";
  try {
    return new Intl.DateTimeFormat("en-AU", {
      timeZone: "Australia/Sydney",
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(new Date(value));
  } catch {
    return value;
  }
}

function eventLabel(type) {
  if (type === "incident") return "Incident";
  if (type === "patrol_start") return "Patrol Start";
  if (type === "patrol_checkpoint") return "Checkpoint";
  if (type === "patrol_end") return "Patrol Complete";
  return type || "Audit Event";
}

function badgeClass(type) {
  if (type === "incident") return "bg-red-100 text-red-800 border-red-200";
  if (type === "patrol_start") return "bg-blue-100 text-blue-800 border-blue-200";
  if (type === "patrol_end") return "bg-green-100 text-green-800 border-green-200";
  return "bg-amber-100 text-amber-800 border-amber-200";
}

export default function AuditTimelinePage() {
  const [date, setDate] = useState("");
  const [siteName, setSiteName] = useState("");
  const [keyword, setKeyword] = useState("");
  const [eventType, setEventType] = useState("");
  const [limit, setLimit] = useState("50");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [error, setError] = useState("");

  const stats = useMemo(() => {
    return {
      total: events.length,
      incidents: events.filter((e) => e.event_type === "incident").length,
      patrols: events.filter((e) => String(e.event_type || "").startsWith("patrol")).length,
    };
  }, [events]);

  async function apiFetch(path, options = {}) {
    const token = getToken();
    const res = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.headers || {}),
      },
    });

    const text = await res.text();
    let data = {};
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      throw new Error(text || "Invalid server response");
    }

    if (!res.ok || data.ok === false) {
      throw new Error(data.message || data.detail || "Request failed");
    }

    return data;
  }

  async function searchEvents() {
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams();
      if (date) params.set("date", date);
      if (siteName) params.set("site_name", siteName);
      if (keyword) params.set("q", keyword);
      if (eventType) params.set("event_type", eventType);
      params.set("limit", limit || "50");

      const data = await apiFetch(`/api/audit-timeline/search?${params.toString()}`);
      setEvents(data.events || []);
    } catch (err) {
      setError(err.message || "Failed to load audit timeline");
    } finally {
      setLoading(false);
    }
  }

  async function rebuildTimeline() {
    setRebuilding(true);
    setError("");

    try {
      const params = new URLSearchParams();
      if (date) params.set("date", date);

      await apiFetch(`/api/audit-timeline/rebuild?${params.toString()}`, {
        method: "POST",
      });

      await searchEvents();
    } catch (err) {
      setError(err.message || "Failed to rebuild audit timeline");
    } finally {
      setRebuilding(false);
    }
  }

  useEffect(() => {
    searchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Audit Timeline</h1>
        <p className="text-gray-600 mt-1">
          Search patrol activity, incidents, GPS evidence and client audit events in one timeline.
        </p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Date</label>
            <input className="w-full border rounded-lg px-3 py-2 text-black placeholder:text-zinc-400" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Site</label>
            <input className="w-full border rounded-lg px-3 py-2 text-black placeholder:text-zinc-400" placeholder="HQ, MRRF, HWC" value={siteName} onChange={(e) => setSiteName(e.target.value)} />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Keyword</label>
            <input className="w-full border rounded-lg px-3 py-2 text-black placeholder:text-zinc-400" placeholder="snake, gate, theft" value={keyword} onChange={(e) => setKeyword(e.target.value)} />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Type</label>
            <select className="w-full border rounded-lg px-3 py-2 text-black placeholder:text-zinc-400" value={eventType} onChange={(e) => setEventType(e.target.value)}>
              <option value="">All</option>
              <option value="incident">Incident</option>
              <option value="patrol_start">Patrol Start</option>
              <option value="patrol_checkpoint">Checkpoint</option>
              <option value="patrol_end">Patrol Complete</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Limit</label>
            <select className="w-full border rounded-lg px-3 py-2 text-black placeholder:text-zinc-400" value={limit} onChange={(e) => setLimit(e.target.value)}>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="300">300</option>
            </select>
          </div>

          <div className="flex items-end gap-2">
            <button onClick={searchEvents} disabled={loading} className="bg-gray-900 text-white px-4 py-2 rounded-lg w-full disabled:opacity-60">
              {loading ? "Searching..." : "Search"}
            </button>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-3 items-center">
          <button onClick={rebuildTimeline} disabled={rebuilding} className="border border-gray-300 px-4 py-2 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-60">
            {rebuilding ? "Rebuilding..." : date ? "Rebuild selected date" : "Rebuild last 30 days"}
          </button>

          <div className="text-sm text-gray-600">
            Results: <strong>{stats.total}</strong> | Incidents: <strong>{stats.incidents}</strong> | Patrol events: <strong>{stats.patrols}</strong>
          </div>
        </div>

        {error && <div className="mt-4 bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 text-sm">{error}</div>}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {events.length === 0 && !loading ? (
          <div className="p-8 text-center text-gray-500">No audit events found. Try changing filters or rebuilding the timeline.</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {events.map((event) => {
              const lat = event.location?.lat;
              const lng = event.location?.lng;
              const hasGps = lat !== null && lat !== undefined && lng !== null && lng !== undefined;

              return (
                <div key={event._id || event.event_key} className="p-4 hover:bg-gray-50">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                    <div>
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className={`text-xs font-bold px-2 py-1 rounded-full border ${badgeClass(event.event_type)}`}>
                          {eventLabel(event.event_type)}
                        </span>
                        <span className="text-sm font-semibold text-gray-900">{formatSydney(event.timestamp)}</span>
                        {event.reference_code && <span className="text-xs text-gray-500">Ref: {event.reference_code}</span>}
                      </div>

                      <h3 className="font-semibold text-gray-900">
                        {event.site_name || "Unknown Site"}
                        {event.checkpoint_name ? ` — ${event.checkpoint_name}` : ""}
                      </h3>

                      <p className="text-gray-700 mt-1 whitespace-pre-wrap">{event.summary || "No summary recorded"}</p>

                      <div className="text-xs text-gray-500 mt-2 flex flex-wrap gap-3">
                        {event.officer_name && <span>Officer: {event.officer_name}</span>}
                        {event.status && <span>Status: {event.status}</span>}
                        {event.source && <span>Source: {event.source}</span>}
                        {hasGps && <span>GPS: {lat}, {lng}</span>}
                        {event.photos?.length > 0 && <span>Photos: {event.photos.length}</span>}
                      </div>
                    </div>

                    {hasGps && (
                      <a href={`https://www.google.com/maps?q=${lat},${lng}`} target="_blank" rel="noreferrer" className="text-sm text-blue-700 underline whitespace-nowrap">
                        Open Map
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
